import { useEffect } from "react";
import { recordSdkBreadcrumb, type SdkBreadcrumbData } from "~/lib/telemetry";
import { isUndecryptableTimelineEvent } from "~/lib/message-utils";
import type { TimelineEvent } from "~/lib/matrix";

const emittedSignatures = new Set<string>();

function countByReason(events: readonly TimelineEvent[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const event of events) {
    if (!isUndecryptableTimelineEvent(event)) continue;
    const key = event.utdWithheldCode ?? event.utdReason ?? "unknown";
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

function signatureFor(counts: Map<string, number>): string {
  return [...counts.entries()]
    .sort((left, right) => left[0].localeCompare(right[0]))
    .map(([key, count]) => `${key}=${count}`)
    .join(",");
}

function dataFromSignature(signature: string): SdkBreadcrumbData {
  const data: SdkBreadcrumbData = {};
  let total = 0;
  for (const pair of signature.split(",")) {
    if (!pair) continue;
    const [key, rawCount] = pair.split("=");
    const count = Number(rawCount);
    data[`count:${key}`] = count;
    total += count;
  }
  data.total = total;
  return data;
}

export function useUtdReasonBreadcrumb(roomId: string, events: readonly TimelineEvent[]): void {
  const signature = signatureFor(countByReason(events));
  useEffect(() => {
    if (!signature) return;
    const once = `${roomId}|${signature}`;
    if (emittedSignatures.has(once)) return;
    emittedSignatures.add(once);
    recordSdkBreadcrumb(
      "matrix.utd-reasons",
      "undecryptable events by reason",
      null,
      dataFromSignature(signature),
    );
  }, [roomId, signature]);
}

export function __resetUtdReasonBreadcrumbsForTests(): void {
  emittedSignatures.clear();
}
